import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { postFinderList } from '../api';
import { finder4Transform } from '../api/resyApiTransformer';
import { loadResyConfigThunk } from './resyConfigSlice';
import { processVenueReservations } from '../utils/venueReservationsSliceUtils';
import { userLogoutThunk } from './userSlice';
import Logger from '../utils/logger';

const initialData = {
  activeNotifications: {},
  notifyForUnavailable: [],
  partnershipBranding: null,
  query: {},
  reservations: [],
  templates: {},
  platinumNight: null,
};

const normalizeFinderParams = (params) => {
  const normalized = { ...params };
  const cutoff = params.service_day_cutoff || '05:00';
  const parsedDay = moment(params.day, 'YYYY-MM-DD', true);
  const parsedTime = moment(params.time_filter, 'HH:mm', true);
  const parsedCutoff = moment(cutoff, 'HH:mm', true);

  // Finder treats post-midnight times as part of the previous service day for overnight shifts.
  if (parsedDay.isValid() && parsedTime.isValid() && parsedCutoff.isValid() && parsedTime.isBefore(parsedCutoff)) {
    normalized.day = parsedDay.subtract(1, 'day').format('YYYY-MM-DD');
  }

  return normalized;
};

const loadVenueReservationsThunk = createAsyncThunk(
  'venueReservations/loadVenueReservations',
  async ({ params }, { dispatch, rejectWithValue }) => {
    const normalizedParams = normalizeFinderParams(params);
    const data = {
      lat: 0,
      long: 0,
      day: normalizedParams.day,
      party_size: normalizedParams.party_size,
      venue_id: normalizedParams.venue_id,
      time_filter: normalizedParams.time_filter,
    };
    /* istanbul ignore else */
    if (typeof normalizedParams.resy_token === 'string') {
      data.resy_token = normalizedParams.resy_token;
    }

    try {
      const response = await postFinderList(data);
      return response;
    } catch (error) {
      const message = error?.data?.message;
      const status = error?.status;
      /* istanbul ignore else */
      if (status === 403) {
        return rejectWithValue({ doubleBookingError: message }, { rejected: true });
      }
      /* istanbul ignore else */
      if (status === 419) {
        await dispatch(userLogoutThunk());
        return rejectWithValue(error, { rejected: true });
      }
      return rejectWithValue(error, { rejected: true });
    }
  }
);

let pendingPromise = null;

const venueReservationsSlice = createSlice({
  name: 'venueReservations',
  initialState: {
    status: 'idle',
    templates: {},
    data: initialData,
  },
  reducers: {
    updateVenueReservations: (state, action) => {
      const { payload } = action;
      state.data = payload.data;
      state.templates = { ...state.templates, ...payload.data.templates };
      state.status = payload.status;
    },
    updateStatus: (state, action) => {
      state.status = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadVenueReservationsThunk.pending, (state) => {
      state.status = 'pending';
      state.data = initialData;
    });
    builder.addCase(loadVenueReservationsThunk.fulfilled, () => {
      pendingPromise = null;
    });
    builder.addCase(loadVenueReservationsThunk.rejected, (state, action) => {
      pendingPromise = null;
      state.error = action.payload;
      state.status = 'error';
    });
  },
});

const { updateVenueReservations } = venueReservationsSlice.actions;
const getVenueReservationsThunk = ({ params, userLogout }) => (dispatch, getState) => {
  const { resyConfig } = getState();
  const { venue_id: venueId } = params;
  let venueReservationData;
  if (pendingPromise && pendingPromise.abort) {
    pendingPromise.abort();
    pendingPromise = null;
  }
  const venueReservationsPromise = dispatch(loadVenueReservationsThunk({ params, userLogout }))
    .unwrap()
    .then((payload) => finder4Transform(payload))
    .then((data) => {
      venueReservationData = data;
      if (!resyConfig.config) {
        return dispatch(loadResyConfigThunk());
      }
      return Promise.resolve({});
    })
    .then(() => {
      if (venueReservationData?.results?.venues?.length) {
        const { config: { service_types: serviceTypes } } = getState().resyConfig;
        const processedData = processVenueReservations(venueReservationData, serviceTypes, venueId);
        const { query } = venueReservationData;
        return dispatch(updateVenueReservations({ data: { query, ...processedData }, status: 'success' }));
      }
      return dispatch(updateVenueReservations({ data: initialData, status: 'success' }));
    })
    .catch((err) => {
      Logger.error('[ERROR] loadVenueReservationsThunk', err);
    });

  pendingPromise = venueReservationsPromise;
  return venueReservationsPromise;
};
export {
  loadVenueReservationsThunk,
  getVenueReservationsThunk,
};

export default venueReservationsSlice.reducer;
